import { Fragment } from 'react';

import {
  Box,
  Chip as MuiChip,
  Theme,
  Typography,
  styled,
} from '@material-ui/core';

import { format as formatDate } from 'date-fns';
import _ from 'lodash';

import { ObjUtils } from '@spotify/confidence-core-react';
import {
  InputMaybe,
  MetricFilterExpressionFragment,
  MetricsV1FilterCriteriaEntryInput,
  MetricsV1FilterCriterionAttributeCriterionInput,
  MetricsV1FilterInput,
  MetricsV1FilterValueNull,
} from '@spotify/confidence-graphql';

import { ValueType, getSelectedOperator } from './Criteria';
import { SetOp, getValueType } from './useMetricFilters';

export const MetricFilterPreview = ({
  filter,
}: {
  filter?: InputMaybe<MetricsV1FilterInput>;
}) => {
  if (!filter) return null;
  if (filter.criteria.length === 0)
    return <Typography variant="body1">No filters</Typography>;
  return (
    <ExpressionPreview
      criterias={filter.criteria}
      expression={filter.expression}
      depth={0}
    />
  );
};

const Chip = styled(MuiChip)({
  height: 'auto',
  minHeight: '24px',
});
const Operands = styled('div')(
  ({ theme, root }: { theme: Theme; root: boolean }) => ({
    gridGap: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    border: root ? 'none' : `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: root ? 0 : theme.spacing(1),
    marginTop: theme.spacing(0.5),
  }),
);

function ExpressionPreview({
  expression,
  criterias,
  depth,
}: {
  expression: MetricFilterExpressionFragment;
  criterias?: InputMaybe<MetricsV1FilterCriteriaEntryInput[]>;
  depth: number;
}) {
  if (expression.and || expression.or) {
    const op = Object.keys(ObjUtils.cleanNulls(expression))[0] as SetOp;
    const operands = expression[op]?.operands ?? [];
    return (
      <Operands root={depth === 0}>
        {operands.map((v, idx) => {
          const isLastCriterion = idx === 0;
          return (
            <Fragment key={`op-${idx}`}>
              {!isLastCriterion && (
                <Typography variant="body2">{_.capitalize(op)}</Typography>
              )}
              <ExpressionPreview
                expression={v}
                criterias={criterias}
                depth={depth + 1}
              />
            </Fragment>
          );
        })}
      </Operands>
    );
  }
  if (expression.ref || expression.not) {
    const ref = expression.not?.ref || expression.ref;
    const criteraValue = (criterias ?? []).find(c => c.key === ref);
    if (!criteraValue || !criteraValue.value) return null;
    const { attribute, ...values } = criteraValue.value?.attribute ?? {};

    const valueType = getValueType(values);
    if (!valueType || !criteraValue.value.attribute)
      return <span>Invalid rule type</span>;

    const op = getSelectedOperator({
      attribute: criteraValue.value.attribute,
      isNot: !!expression.not,
      valueType,
    });

    return (
      <Chip
        size="small"
        label={
          <Box display="flex" gridGap={8} alignItems="center">
            <Typography variant="body2">{attribute}</Typography>
            <Typography variant="body2">{op?.label}</Typography>
            {valueType !== 'nullValue' && (
              <Typography variant="body2" style={{ whiteSpace: 'normal' }}>
                {getValues(criteraValue.value.attribute, valueType)}
              </Typography>
            )}
          </Box>
        }
      />
    );
  }
  return null;
}

function getValues(
  attribute: InputMaybe<MetricsV1FilterCriterionAttributeCriterionInput>,
  valueType: ValueType,
): string | number | null | undefined {
  const parseValue = (
    v?: Maybe<string | number | boolean | MetricsV1FilterValueNull>,
  ) => {
    if (!v) return null;
    if (valueType === 'nullValue') {
      return 'null';
    }
    if (valueType === 'timestampValue' && !isNaN(Date.parse(v.toString()))) {
      return formatDate(new Date(v.toString()), 'yyyy-MM-dd HH:mm');
    }
    return v.toString();
  };
  if (attribute?.eqRule) {
    if (valueType === 'boolValue') {
      return attribute.eqRule.value[valueType] === true ? 'true' : 'false';
    }
    if (valueType === 'nullValue') {
      return 'null';
    }
    return attribute.eqRule.value[valueType];
  }

  if (attribute?.setRule) {
    return attribute.setRule.values.map(v => v[valueType]).join(', ');
  }
  if (attribute?.rangeRule) {
    const v1 =
      parseValue(attribute.rangeRule.startExclusive?.[valueType]) ??
      parseValue(attribute.rangeRule.startInclusive?.[valueType]);
    const v2 =
      parseValue(attribute.rangeRule.endExclusive?.[valueType]) ??
      parseValue(attribute.rangeRule.endInclusive?.[valueType]);

    const values = [];
    if (!!v1) values.push(v1.toString());
    if (!!v2) values.push(v2.toString());

    return values.join(' and ');
  }
  return null;
}
